import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

import { environment } from 'environment/environment';

import { Crp } from 'modules/shared/models/crp.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';
import { IndividualRecordPayload, GetRecordOtherDataPayload, UpdateRecordOtherDataPayload, RecordOtherDataResponse } from 'store/models/record.models';
import { CrpResponse } from 'store/models/crp.models';

import { ApiAbstractService } from './api-abstract.service';
import { CustomerService } from './customer.service';

@Injectable()
export class RecordService extends ApiAbstractService {

  private apiBase: string = environment.API_BASE;

  constructor(
    private httpClient: HttpClient,
    private customerService: CustomerService
  ) {
    super(environment.API_BASE);
  }

  getCustomerRecord<T extends IndividualRecord | CorporateRecord>(customerId: number): Observable<T> {
    return this.customerService.getCustomerCrps({
      customerId: customerId,
      limit: 1,
      primary: true
    }).pipe(
      switchMap((crps: CrpResponse) => {
        const customerCrp: Crp = crps.results[0];
        if (customerCrp.recordType === 'INDIVIDUAL') {
          return this.getIndividualRecord(customerCrp.record);
        } else {
          return this.getCorporateRecord(customerCrp.record);
        }
      }),
      map((record: IndividualRecord | CorporateRecord) => {
        return <T>record;
      })
    );
  }

  getRecords(name: string = '') {
    return this.httpClient.get(`${this.apiBase}/api/records/?search=${name}`);
  }

  getIndividualRecords(payload: IndividualRecordPayload) {
    return this.httpClient.get(this.buildUrl(`/api/individual_records/`, payload));
  }

  getCorporateRecords(payload: IndividualRecordPayload) {
    return this.httpClient.get(this.buildUrl(`/api/corporate_records/`, payload));
  }

  getRecord(recordId: number) {
    return this.httpClient.get(`${this.apiBase}/api/records/${recordId}/`);
  }

  getRecordByReferenceId(referenceId: number) {
    return this.httpClient.get(`${this.apiBase}/api/records/?reference_id=${referenceId}`);
  }

  getIndividualRecord(recordId: number): Observable<IndividualRecord> {
    return this.httpClient.get<IndividualRecord>(`${this.apiBase}/api/individual_records/${recordId}/`);
  }

  getCorporateRecord(recordId: number): Observable<CorporateRecord> {
    return this.httpClient.get<CorporateRecord>(`${this.apiBase}/api/corporate_records/${recordId}/`);
  }

  createIndividualRecord(newIndividualRecord: IndividualRecord) {
    return this.httpClient.post(`${this.apiBase}/api/individual_records/`, newIndividualRecord);
  }

  createCorporateRecord(newCorporateRecord: CorporateRecord) {
    return this.httpClient.post(`${this.apiBase}/api/corporate_records/`, newCorporateRecord);
  }

  updateIndividualRecord(recordId: number, newIndividualRecord: IndividualRecord) {
    return this.httpClient.put(`${this.apiBase}/api/individual_records/${recordId}/`, newIndividualRecord);
  }

  updateCorporateRecord(recordId: number, newCorporateRecord: CorporateRecord) {
    return this.httpClient.put(`${this.apiBase}/api/corporate_records/${recordId}/`, newCorporateRecord);
  }

  getCountries(name: string = ''): Observable<string[]> {
    return this.httpClient.get<string[]>(`${this.apiBase}/api/records/countries/?search=${name}`);
  }

  getSourceOfFunds(): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/records/source_of_funds/`);
  }

  getSSIC(name: string = ''): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/records/ssic/?search=${name}`);
  }

  getSSICMigration(name: string = ''): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/records/ssic/?search=${name}&legacy=true`);
  }

  getSSOC(name: string = ''): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/individual_records/ssoc/?search=${name}`);
  }

  getSSOCMigration(name: string = ''): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/individual_records/ssoc/?search=${name}&legacy=true`);
  }

  getTitles(name: string = ''): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/individual_records/titles/?search=${name}`);
  }

  getIDTypes(name: string = ''): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/individual_records/id_types/?search=${name}`);
  }

  getEntityTypes(name: string = ''): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/corporate_records/entity_types/?search=${name}`);
  }

  getOwnershipLayers(): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/corporate_records/ownership_layers/`);
  }

  getRoles(): Observable<string[]> {
    return this.httpClient.get<Array<string>>(`${this.apiBase}/api/crps/roles/`);
  }

  isIncorporated(recordId: number): Observable<boolean> {
    return this.getCorporateRecord(recordId).pipe(map((record: CorporateRecord) => record.isIncorporated));
  }

  getOtherData(getOtherDataReqBody: GetRecordOtherDataPayload): Observable<RecordOtherDataResponse> {
    let params: HttpParams = new HttpParams();
    const url: string = this.apiBase + '/api/others_data/';

    if (getOtherDataReqBody) {
      const reqBodyEntries: Array<[string, any]> = Object.entries(getOtherDataReqBody);

      reqBodyEntries.forEach(([key, value]: [string, any]) => {
        if (value !== undefined) {
          params = params.append(key, value);
        }
      });
    }

    return this.httpClient.get<RecordOtherDataResponse>(url, { params: params });
  }

  updateOtherData(updateOtherDataReqBody: UpdateRecordOtherDataPayload): Observable<RecordOtherDataResponse> {
    const url: string = this.apiBase + `/api/others_data/${updateOtherDataReqBody.otherDataId}/`;
    delete updateOtherDataReqBody.otherDataId;

    return this.httpClient.patch<RecordOtherDataResponse>(url, updateOtherDataReqBody);
  }
}
